import {
  Box,
  Button,
  DialogContent,
  DialogActions,
  Typography,
  FormControl,
  InputLabel,
} from "@mui/material";
import dayjs from "../../../../../utils/dayjs";
import { formatLastUpdated } from "../../../../../utils/tableUtil";
import { FLEX_COL_FILL } from "../../../../../themes/sbux/components";

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useGridApiRef } from "@mui/x-data-grid";
import { useAppDispatch, useAppSelector } from "../../../../../store/types";

import {
  dataNormalizer,
  normalizeContentSpec,
  getDataColumns,
  getRowAttributes,
  normalizeTableList,
  getDefaultSortModel,
  type ContentSpec,
  type ColumnSpec,
} from "./utils";

import {
  getTableListData,
  getTableMetaData,
  createOrUpdateListData,
  type ListDataPayload,
} from "../../../../../services/dataMgt";

import SbuxDialog2 from "../../../../../components/SbuxDialog2";
import SbuxLoader from "../../../../../components/SbuxLoader";
import SbuxSelect from "../../../../../components/SbuxSelect";
import SbuxContentCopy from "../../../../../components/SbuxContentCopy";

import {
  SbuxGenericGrid,
  SbuxGridToolbar,
} from "../../../../../components/SbuxDataGrid/SbuxGenericGrid";
import type { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { StyledDeleteButton } from "../../styled";
import SbuxTranslate from "../../../../../components/SbuxTranslate";
import DataManagementDialog from "../../common/DataManagementDialog";
import DateRangeFilterDialog from "../../common/DateRangeFilterDialog";

import { type MenuItem as SelectMenuItem } from "../../../../../components/SbuxSelect";
import { clearState } from "../../../../../slices/dataMgtSlice";
import { getInstanceIdSelector } from "../../../../../selectors/userInstanceSelector";
import {
  getMetaConfigDataSelector,
  getTableListDataSelector,
  getTableListLoadingSelector,
  getMetadataLoadingSelector,
} from "../../../../../selectors/dataMgtSelector";
import { getTimezoneSelector } from "../../../../../selectors/settingsSelector";

import styles from "./styles";
import useCss from "../../../../../hooks/useCss";
import FilterListIcon from "@mui/icons-material/FilterList";
import { IconButton } from "@mui/material";

interface MetaTable {
  tableName?: string;
  friendlyName?: string;
  sortDirection?: string;
  crudOperations?: string[];
  config?: { attributes?: Record<string, unknown>[] };
  [key: string]: unknown;
}

interface MetaConfigData {
  tables?: MetaTable[];
  [key: string]: unknown;
}

const DataManagementTabs = () => {
  const classes = useCss(styles);
  const dispatch = useAppDispatch();

  /* Selectors */
  const instanceId = useAppSelector(getInstanceIdSelector);
  const metaConfigData = useAppSelector(
    getMetaConfigDataSelector,
  ) as unknown as MetaConfigData | null;
  const tableListData = useAppSelector(getTableListDataSelector) as Record<
    string,
    unknown
  >[];
  const tableListLoading = useAppSelector(
    getTableListLoadingSelector,
  ) as boolean;
  const metadataLoading = useAppSelector(getMetadataLoadingSelector) as boolean;
  const timezone = useAppSelector(getTimezoneSelector) as string;
  const timezoneRef = useRef(timezone);
  timezoneRef.current = timezone;

  /* Individual state declarations */
  const [openDialogue, setOpenDialogue] = useState(false);
  const [openDeleteDialogue, setOpenDeleteDialogue] = useState(false);
  const [row, setRow] = useState<Record<string, unknown> | null>(null);
  const [filterList, setFilterList] = useState<SelectMenuItem[]>([]);
  const [tableName, setTableName] = useState<string | null>(null);
  const [tableFriendlyName, setTableFriendlyName] = useState<string | null>(
    null,
  );
  const [addNew, setAddNew] = useState(false);
  const [defaultValue, setdefaultValue] = useState("");
  const [isDisableAddNewButton, setIsDisableAddNewButton] = useState(true);
  const [crudOperations, setCrudOperations] = useState<string[]>([]);
  const isCrudRefetch = useRef(false);
  const isLoading =
    !isCrudRefetch.current && (tableListLoading || metadataLoading);
  const [contentSpec, setContentSpec] = useState<Partial<ContentSpec>>({});
  const selectedTable = useRef<string | null>(null);
  const [sortModel, setSortModel] = useState<{ field: string; sort: string }[]>(
    [],
  );
  const apiRef = useGridApiRef();
  const [selectedItem, setSelectedItem] = useState<MetaTable | null>(null);
  const [tableData, setTableData] = useState<Record<string, unknown>[]>([]);

  const [dateFilterOpen, setDateFilterOpen] = useState(false);
  const [dateFilterField, setDateFilterField] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>({
    startDate: null,
    endDate: null,
  });

  // Memoize normalized rows to prevent unnecessary grid reloads
  const normalizedRows = useMemo(
    () =>
      contentSpec.columns?.length
        ? dataNormalizer(tableData, contentSpec.columns, timezone)
        : [],
    [tableData, contentSpec.columns, timezone],
  );

  const handleOpenDateFilter = (field: string) => {
    setDateFilterField(field);
    setDateFilterOpen(true);
  };

  const handleApplyDateFilter = (
    startDate: Date | null,
    endDate: Date | null,
    field: string | null,
  ) => {
    setDateRange({ startDate, endDate });
    if (!field) return;

    const filtered = tableListData.filter((row) => {
      const value = new Date(row[field] as string);
      if (isNaN(value.getTime())) return false;
      return (
        value >= (startDate ?? new Date(0)) && value <= (endDate ?? new Date())
      );
    });

    setTableData(filtered);
  };

  /**
   * Dialog configuration
   */
  const dialogConfig = useMemo(
    () => ({
      title: "Delete Item",
      hasBackdrop: false,
      asModal: true,
    }),
    [],
  );

  /**
   * Retrieves selected table's data
   * @param  {string}  tableName               selected table's name
   */
  const fetchTableData = useCallback(
    async (tableName: string, silent = false) => {
      if (tableName) {
        isCrudRefetch.current = silent;
        await dispatch(getTableListData(tableName));
        isCrudRefetch.current = false;
      }
    },
    [dispatch],
  );

  /**
   * Retrieves metadata config data
   */
  const fetchMetadataConfig = useCallback(async () => {
    await dispatch(getTableMetaData());
  }, [dispatch]);

  useEffect(() => {
    fetchMetadataConfig();

    return () => {
      dispatch(clearState());
    };
  }, [instanceId, dispatch, fetchMetadataConfig]);

  useEffect(() => {
    setTableData([...tableListData]);
    sortTableData(sortModel);
  }, [tableListData]);

  /**
   * Data action
   */
  useEffect(() => {
    if (metaConfigData?.tables?.length) {
      const tableList = normalizeTableList(
        metaConfigData?.tables as Array<{
          friendlyName?: string;
          tableName?: string;
          uiPosition?: number;
        }>,
      );
      const filterItems: SelectMenuItem[] = tableList
        .filter(
          (
            item,
          ): item is {
            name: string;
            value: string;
            uiPosition: number | undefined;
          } => !!item.name && !!item.value,
        )
        .map((item) => ({ name: item.name, value: item.value }));

      setFilterList(filterItems);

      const defaultTable =
        tableName || selectedTable.current || tableList[0]?.value;
      if (defaultTable) {
        handleTableSelection(defaultTable);
      }
    }
  }, [metaConfigData /*selectedTable.current, tableName*/]);

  /**
   * Action button definition spec
   * @return {Object} button definition spec
   */
  const actionButtonSpec = (): ColumnSpec => {
    return {
      headerName: "Action",
      field: "action",
      id: "action",
      type: "actions",
      sortable: false,
      fieldVisible: true,
      renderCell: (params: Record<string, unknown>) => (
        <StyledDeleteButton
          color="primary"
          onClick={(event: React.MouseEvent) => {
            event.stopPropagation();
            handleDeleteRowClick(params.row as Record<string, unknown>);
          }}
          disableFocusRipple
          disableRipple
        />
      ),
      display: "flex",
      align: "center",
    };
  };

  /**
   * Add custom filter function for number and boolean type
   */
  const addQuickFilter = useCallback((cs: ContentSpec): ContentSpec => {
    if (!cs?.columns) {
      return cs;
    }
    return {
      ...cs,
      columns: cs.columns.map((columnObj: ColumnSpec) => {
        if (columnObj?.type === "number" || columnObj?.type === "boolean") {
          return {
            ...columnObj,
            getApplyQuickFilterFn:
              (quickFilterValue: unknown) => (value: unknown) => {
                // Convert both the quick filter value and the cell value to string for comparison
                return String(value)
                  .toLowerCase()
                  .includes(String(quickFilterValue).toLowerCase());
              },
          };
        }
        return columnObj;
      }),
    };
  }, []);

  /**
   * Handle table selection event
   * @param  {string} selectedTableName               table name
   */
  const handleTableSelection = (selectedTableName: string) => {
    if (!selectedTableName) return;

    const selectedItem = metaConfigData?.tables?.find(
      (item) => item.tableName === selectedTableName,
    );

    if (selectedItem) {
      // Clear data when switching tables
      if (selectedTableName !== tableName) {
        setTableData([]);
      }

      if (selectedItem?.tableName) fetchTableData(selectedItem.tableName);

      let newContentSpec = normalizeContentSpec(
        selectedItem?.config?.attributes ?? [],
      );

      newContentSpec = addQuickFilter(newContentSpec);
      newContentSpec.columns = newContentSpec.columns.map((col: ColumnSpec) => {
        const baseConfig: ColumnSpec = {
          ...col,
          minWidth: col.minWidth || 130,
        };

        if (col.type === "date" || col.type === "datetime") {
          return {
            ...baseConfig,
            filterable: false,
            width: 500,
            minWidth: 250,
            cellClassName: "date-column-cell",
            // Ensure we return a formatted string, not a Date object, to avoid React child errors
            // MUI X Data Grid v6+ signature: (value, row, column, apiRef) => any
            // Always parse with dayjs.utc() directly on the raw string so that
            // values without a Z suffix are treated as UTC (not local time).
            valueGetter: (value: unknown) => {
              if (!value) return "";
              const parsed = dayjs.utc(String(value));
              if (!parsed.isValid()) return "";
              return parsed
                .tz(timezoneRef.current)
                .format("YYYY-MM-DD hh:mm A");
            },
            renderHeader: () => (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <span
                  style={{
                    fontWeight: 500,
                    fontSize: "15px",
                    color: "#f2f0eb",
                  }}
                >
                  {col.headerName}
                </span>
                <IconButton
                  size="small"
                  onClick={() => col.field && handleOpenDateFilter(col.field)}
                  color="primary"
                  aria-label="Filter"
                >
                  <FilterListIcon />
                </IconButton>
              </Box>
            ),
          };
        }

        // For non-date columns, add copy-on-hover UI
        // Avoid modifying action-like columns
        if (col.field !== "action") {
          const originalRender = baseConfig.renderCell;

          const renderCell = (params: GridRenderCellParams) => {
            const value = params?.value ?? "";
            // Compute copy text: for lastUpdated we want the friendly formatted string
            const copyText =
              params.field === "lastUpdated"
                ? formatLastUpdated(
                    {
                      lastUpdated: (params.row as Record<string, unknown>)
                        ?.lastUpdated as string | null | undefined,
                      lastUpdatedBy: (params.row as Record<string, unknown>)
                        ?.lastUpdatedBy as string | null | undefined,
                    },
                    timezoneRef.current,
                  )
                : String(params.value ?? "");
            // For lastUpdated, reuse copyText (already timezone-aware).
            // For others, delegate to the existing renderer or fall back to plain string.
            const contentNode: React.ReactNode =
              params.field === "lastUpdated" ? (
                <span>{copyText}</span>
              ) : originalRender ? (
                (originalRender(
                  params as unknown as Record<string, unknown>,
                ) as React.ReactNode)
              ) : (
                <span>{String(value)}</span>
              );

            return (
              <div className={classes.cellContainer}>
                <div className={classes.cellContent}>{contentNode}</div>
                <SbuxContentCopy
                  content={copyText}
                  className={`copy-icon ${classes.cellCopyIcon}`}
                />
              </div>
            );
          };

          return {
            ...baseConfig,
            cellClassName: "copyable-cell",
            renderCell,
          };
        }

        return baseConfig;
      }) as ColumnSpec[];

      const tempCrudOperations = selectedItem?.crudOperations ?? [];
      if (tempCrudOperations.includes("DELETE")) {
        const buttonSpec = actionButtonSpec();
        newContentSpec.columns.push(buttonSpec);
      }

      setContentSpec(newContentSpec);
      setSelectedItem(selectedItem);

      setCrudOperations(tempCrudOperations);
      if (tempCrudOperations.includes("CREATE")) {
        setIsDisableAddNewButton(false);
      } else {
        setIsDisableAddNewButton(true);
      }

      setTableFriendlyName(selectedItem?.friendlyName ?? null);
      setdefaultValue(selectedTableName);

      // preventing re-renders
      if (selectedTableName !== tableName) {
        selectedTable.current = selectedTableName;
        setTableName(selectedTableName);
        // Reset date filter when switching tables
        setDateRange({ startDate: null, endDate: null });
        setDateFilterField(null);
      }
    }
  };

  const handleModal = useCallback((isNew = false) => {
    setAddNew(isNew);
    setOpenDialogue((prevState) => !prevState);
  }, []);

  /**
   * Handle onRow click event. Open a modal passing row data to edit
   * @param  {Object} params               row params
   */
  const handleOnRowClick = useCallback(
    (params: { row: Record<string, unknown> }) => {
      setRow({ ...params.row });
      handleModal();
    },
    [handleModal],
  );

  const handleOnCellClick = useCallback(
    (param: { field?: string }, event: { stopPropagation: () => void }) => {
      param.field === "action" && event.stopPropagation();
    },
    [],
  );

  const handleSave = useCallback(
    async (listDataPayload: ListDataPayload) => {
      if (listDataPayload?.isEdit) {
        const originalAttributes = getRowAttributes(row);
        (
          listDataPayload.payload as Record<string, unknown>
        ).originalAttributes = originalAttributes;
      }

      const result = await dispatch(createOrUpdateListData(listDataPayload));
      if (result?.meta?.requestStatus === "fulfilled") {
        handleModal();
        setRow(null);
        fetchTableData(listDataPayload.tableName, true);
      }
    },
    [row, dispatch, handleModal, fetchTableData],
  );

  /**
   * Handles the delete confirmation action of a selected row
   */
  const handleDeleteRow = async () => {
    if (!tableName) return;
    const attributes = getRowAttributes(row);

    const listDataPayload: ListDataPayload = {
      payload: { attributes },
      tableName: tableName,
      isDelete: true,
    };

    const result = await dispatch(createOrUpdateListData(listDataPayload));
    if (result?.meta?.requestStatus === "fulfilled") {
      setOpenDeleteDialogue(false);
      fetchTableData(tableName, true);
    }
  };

  useEffect(() => {
    const sortDirection = selectedItem?.sortDirection || "asc";
    const defaultSortModel = getDefaultSortModel(
      contentSpec.columns ?? [],
      sortDirection,
    );
    sortTableData(defaultSortModel);
  }, [contentSpec, selectedItem]);

  /**
   * Handles onClick event for the refresh button
   */
  const handleOnRefreshButtonClick = async () => {
    const sortDirection = selectedItem?.sortDirection || "asc";
    const defaultSortModel = getDefaultSortModel(
      contentSpec.columns ?? [],
      sortDirection,
    );
    sortTableData(defaultSortModel);
    await fetchMetadataConfig();
  };

  /**
   * Handles onDeleteRow click event
   * @param  {Object} row               row to delete
   */
  const handleDeleteRowClick = useCallback((row: Record<string, unknown>) => {
    setRow(row);
    setOpenDeleteDialogue(true);
  }, []);

  /**
   * TableSelect component to describe the rendering of the Datagrid toolbar
   */
  const TableSelect = () => {
    if (!filterList) {
      return null;
    }

    const selectionDropdown = (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
          <SbuxTranslate>{`Make a selection`}</SbuxTranslate>
        </Typography>
        <FormControl sx={{ minWidth: 150, maxWidth: 150 }} size="small">
          <SbuxSelect
            menuItems={filterList}
            handleSelectChange={handleTableSelection}
            value={defaultValue}
          />
        </FormControl>
      </Box>
    );

    return (
      <SbuxGridToolbar apiRef={apiRef} leftContent={selectionDropdown}>
        <Button
          variant="contained"
          size="small"
          onClick={() => handleOnRefreshButtonClick()}
        >
          {<SbuxTranslate>{"Refresh"}</SbuxTranslate>}
        </Button>

        <Button
          variant="contained"
          size="small"
          onClick={() => handleModal(true)}
          disabled={isDisableAddNewButton}
        >
          {<SbuxTranslate>{"Add New"}</SbuxTranslate>}
        </Button>
        <Button variant="contained" size="small" onClick={handleExportToCSV}>
          {<SbuxTranslate>{"Export"}</SbuxTranslate>}
        </Button>
        {dateFilterOpen && (
          <DateRangeFilterDialog
            open={dateFilterOpen}
            onClose={() => setDateFilterOpen(false)}
            onApply={(startDate, endDate) =>
              handleApplyDateFilter(startDate, endDate, dateFilterField)
            }
            initialStartDate={dateRange.startDate}
            initialEndDate={dateRange.endDate}
            includeTime={false}
          />
        )}
      </SbuxGridToolbar>
    );
  };
  /**
   * Export data to CSV
   */
  const handleExportToCSV = () => {
    if (!apiRef?.current) {
      return;
    }

    // Get the current filter model and search text
    const filterModel = apiRef.current.state.filter.filterModel;
    const quickFilterValue = filterModel?.quickFilterValues?.[0] || "";

    /**
     * Get the filtered rows based on current filters and search
     */

    const filteredRows = tableListData.filter((row) => {
      if (quickFilterValue) {
        return Object.values(row).some((value) =>
          String(value).toLowerCase().includes(quickFilterValue.toLowerCase()),
        );
      }
      return true;
    });

    /**
     * Get the selected table's configuration
     */
    const selectedTableConfig = metaConfigData?.tables?.find(
      (item) => item.tableName === tableName,
    );

    /**
     * Map columns to friendly names
     */
    const cols = contentSpec.columns ?? [];
    const exportColumns = cols
      .filter((col) => col.field !== "action")
      .map((col) => ({
        field: col.field,
        headerName:
          ((
            selectedTableConfig?.config?.attributes?.find(
              (attr: Record<string, unknown>) => attr.name === col.field,
            ) as Record<string, unknown> | undefined
          )?.friendlyName as string) || col.field,
      }));

    const normalizedData = dataNormalizer(filteredRows, cols, timezone);

    /**
     * Create CSV content
     */
    const csvContent = [
      exportColumns.map((col) => col.headerName).join(","),
      ...normalizedData.map((row) =>
        exportColumns
          .map((col) => {
            let value = row[col.field as string];
            if (col.field === "phoneNumber" && value) {
              value = value.toString().replace(/['"]+/g, "");
            }
            if (
              value &&
              typeof value === "string" &&
              (value.includes(",") || value.includes('"'))
            ) {
              value = `"${value.replace(/"/g, '""')}"`;
            }
            return value || "";
          })
          .join(","),
      ),
    ].join("\n");

    /**
     * Trigger download
     */
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${tableFriendlyName || tableName}-export.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const sortTableData = (model: { field: string; sort: string }[]) => {
    if (!model || model.length === 0) {
      setSortModel([]);
      return;
    }

    const sortField = model[0].field;
    const sortDirection = model[0].sort;
    setSortModel(model);

    const sortedData = [...tableListData].sort((a, b) => {
      const rawA = a[sortField];
      const rawB = b[sortField];
      let aValue: number | string;
      let bValue: number | string;

      if (sortField === "dateTime") {
        aValue = new Date(rawA as string).getTime();
        bValue = new Date(rawB as string).getTime();
      } else if (typeof rawA === "boolean" && typeof rawB === "boolean") {
        aValue = rawA ? 1 : 0;
        bValue = rawB ? 1 : 0;
      } else if (typeof rawA !== "number" || typeof rawB !== "number") {
        aValue = rawA != null ? String(rawA).toLowerCase() : "";
        bValue = rawB != null ? String(rawB).toLowerCase() : "";
      } else {
        aValue = rawA;
        bValue = rawB;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    setTableData(sortedData);
  };

  return (
    <Box sx={FLEX_COL_FILL}>
      {isLoading ? (
        <SbuxLoader />
      ) : tableData?.length > 0 && contentSpec.columns?.length ? (
        <Box sx={FLEX_COL_FILL}>
          <SbuxGenericGrid
            apiRef={apiRef}
            rows={normalizedRows}
            sortingMode="server"
            columns={contentSpec.columns as unknown as GridColDef[]}
            columnVisibilityModel={contentSpec.columnVisibilityModel}
            labelRowsPerPage={<SbuxTranslate>{"Rows per page"}</SbuxTranslate>}
            labelOf={<SbuxTranslate>{"of"}</SbuxTranslate>}
            searchPlaceholder={<SbuxTranslate>{"Search..."}</SbuxTranslate>}
            loading={isLoading}
            slots={{
              toolbar: TableSelect,
            }}
            sortModel={sortModel}
            onSortModelChange={(model: { field: string; sort: string }[]) =>
              sortTableData(model)
            }
            slotClasses={{
              toolbar: {
                container: classes.toolbarContainer,
              },
              datagrid: {
                container: classes.datagridContainer,
              },
            }}
            onRowClick={handleOnRowClick}
            onCellClick={handleOnCellClick}
            getEstimatedRowHeight={contentSpec.getEstimatedRowHeight}
            getRowHeight={contentSpec.getRowHeight}
            sx={{
              ...contentSpec.sx,
            }}
            clickableRow
          />
        </Box>
      ) : null}
      {openDialogue && (
        <DataManagementDialog
          title={
            addNew
              ? `Create new ${tableFriendlyName}`
              : `Edit ${tableFriendlyName}`
          }
          open={openDialogue}
          toggleDialogOpen={() => setOpenDialogue(false)}
          row={row}
          rows={tableData}
          addNew={addNew}
          tableName={tableName ?? ""}
          onSave={handleSave as (payload: unknown) => void}
          columns={
            getDataColumns(contentSpec.columns ?? []) as unknown as {
              id?: string;
              label?: string;
              type?: string;
              fieldVisible?: boolean;
              uiPosition?: number;
              required?: boolean;
              inputMaxLength?: number;
              lockedForEdit?: boolean;
              partitionKey?: boolean;
              sortKey?: boolean;
              selectValues?: string[];
              [key: string]: unknown;
            }[]
          }
          crudOperations={crudOperations}
        />
      )}

      {openDeleteDialogue && (
        <SbuxDialog2 open={openDeleteDialogue} config={dialogConfig}>
          <DialogContent dividers>
            <Typography variant="h5">
              <SbuxTranslate>
                {"Are you sure you want to delete it?"}
              </SbuxTranslate>
            </Typography>
          </DialogContent>

          <DialogActions>
            <Box className="actionContainer">
              <Button
                variant="contained"
                size="medium"
                onClick={handleDeleteRow}
              >
                <SbuxTranslate>{`Delete`}</SbuxTranslate>
              </Button>
              <Button
                variant="outlined"
                size="medium"
                onClick={() => setOpenDeleteDialogue(false)}
              >
                <SbuxTranslate>{`Cancel`}</SbuxTranslate>
              </Button>
            </Box>
          </DialogActions>
        </SbuxDialog2>
      )}
    </Box>
  );
};

export default DataManagementTabs;
